import type {
  VariantAttribute,
  VariantAttributeOption,
  VariantAttributeFilter,
  CreateVariantAttributePayload,
  UpdateVariantAttributePayload,
  CreateAttributeOptionPayload,
  UpdateAttributeOptionPayload,
} from "@/types/product";

import { useMockStore } from "../store";
import { createId, createPastTimestamp } from "../generators/base";

/**
 * Get all variant attributes with pagination
 */
export function getVariantAttributesMock(
  filters: VariantAttributeFilter = {},
): VariantAttribute[] {
  const store = useMockStore.getState();
  let attributes = [...store.variantAttributes];

  // Apply pagination
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 100;
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;

  return attributes.slice(startIndex, endIndex);
}

/**
 * Get a single variant attribute by ID
 */
export function getVariantAttributeMock(
  attributeId: string,
): VariantAttribute | undefined {
  const store = useMockStore.getState();

  return store.variantAttributes.find((a) => a.id === attributeId);
}

/**
 * Create a new variant attribute
 */
export function createVariantAttributeMock(
  payload: CreateVariantAttributePayload,
): VariantAttribute {
  const store = useMockStore.getState();
  const timestamp = createPastTimestamp(0);
  const attributeId = createId();

  // Create options if provided
  const options = payload.options?.map((opt) => ({
    id: createId(),
    name: opt.name,
    value: opt.value,
    attributeId,
    createdAt: timestamp,
    updatedAt: null,
  }));

  const newAttribute: VariantAttribute = {
    id: attributeId,
    name: payload.name,
    description: payload.description ?? null,
    options: options || [],
    organizationId: store.organization?.id ?? "",
    workspaceId: store.workspaces[0]?.id ?? "",
    createdAt: timestamp,
    updatedAt: null,
  };

  store.addVariantAttribute(newAttribute);

  return newAttribute;
}

/**
 * Update a variant attribute
 */
export function updateVariantAttributeMock(
  attributeId: string,
  payload: UpdateVariantAttributePayload,
): VariantAttribute | undefined {
  const store = useMockStore.getState();
  const attribute = store.variantAttributes.find((a) => a.id === attributeId);

  if (!attribute) return undefined;

  const updates: Partial<VariantAttribute> = {
    name: payload.name,
    description: payload.description,
    updatedAt: new Date().toISOString(),
  };

  store.updateVariantAttribute(attributeId, updates);

  return { ...attribute, ...updates };
}

/**
 * Delete a variant attribute
 */
export function deleteVariantAttributeMock(attributeId: string): boolean {
  const store = useMockStore.getState();
  const exists = store.variantAttributes.some((a) => a.id === attributeId);

  if (exists) {
    store.deleteVariantAttribute(attributeId);

    return true;
  }

  return false;
}

/**
 * Add an option to a select attribute
 */
export function addAttributeOptionMock(
  attributeId: string,
  payload: CreateAttributeOptionPayload,
): VariantAttributeOption {
  const store = useMockStore.getState();
  const timestamp = createPastTimestamp(0);

  const newOption: VariantAttributeOption = {
    id: createId(),
    name: payload.name,
    value: payload.value,
    attributeId,
    createdAt: timestamp,
    updatedAt: null,
  };

  store.addAttributeOption(attributeId, newOption);

  return newOption;
}

/**
 * Update an attribute option
 */
export function updateAttributeOptionMock(
  attributeId: string,
  optionId: string,
  payload: UpdateAttributeOptionPayload,
): VariantAttributeOption | undefined {
  const store = useMockStore.getState();
  const attribute = store.variantAttributes.find((a) => a.id === attributeId);
  const option = attribute?.options?.find((o) => o.id === optionId);

  if (!option) return undefined;

  const updates: Partial<VariantAttributeOption> = {
    name: payload.name,
    value: payload.value,
    updatedAt: new Date().toISOString(),
  };

  store.updateAttributeOption(attributeId, optionId, updates);

  return { ...option, ...updates };
}

/**
 * Delete an attribute option
 */
export function deleteAttributeOptionMock(
  attributeId: string,
  optionId: string,
): boolean {
  const store = useMockStore.getState();
  const attribute = store.variantAttributes.find((a) => a.id === attributeId);
  const exists = attribute?.options?.some((o) => o.id === optionId);

  if (exists) {
    store.deleteAttributeOption(attributeId, optionId);

    return true;
  }

  return false;
}
